import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/InputDefault";
import { Save, X, Plus } from "lucide-react";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useToast } from "@/hooks/use-toast";
import { createTag } from "@/lib/stores/tagsStore";
import { TagItem } from "@/lib/stores/tagsStore";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface AddTagsToGroupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  groupName: string;
  groupColor: string;
  groupDescription?: string;
}

export default function AddTagsToGroup({ 
  isOpen, 
  onClose, 
  onSuccess, 
  groupName, 
  groupColor, 
  groupDescription 
}: AddTagsToGroupProps) {
  const { t } = usePlatformTranslation();
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();

  const [newTags, setNewTags] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedGame || !newTags.trim()) {
      toast({
        title: "Error",
        description: "Please provide at least one tag",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Parse comma-separated tags
      const tagNames = newTags
        .split(",")
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      if (tagNames.length === 0) {
        toast({
          title: "Error",
          description: "Please provide at least one tag",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // Create each tag with the existing group information
      const promises = tagNames.map(tagName =>
        createTag({
          game_id: selectedGame.id,
          name: tagName,
          group: groupName,
          description: groupDescription,
          color: groupColor,
        }, toast)
      );

      await Promise.all(promises);

      toast({
        title: "Success",
        description: `Added ${tagNames.length} tag${tagNames.length > 1 ? 's' : ''} to group "${groupName}"`,
      });

      // Reset form
      setNewTags("");

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error adding tags:", error);
      toast({
        title: "Error",
        description: "Failed to add tags. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNewTags("");
    onClose();
  };

  if (!isOpen) return null;

  return (  
    <div className="space-y-3 p-3 border border-input rounded bg-muted">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">
          Add Tags to "{groupName}"
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          disabled={isSubmitting}
          className="h-6 w-6 p-0"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>

      <div className="space-y-3">
        {/* Tags Input */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            New Tags *
          </label>
          <Input
            value={newTags}
            onChange={(e) => setNewTags(e.target.value)}
            placeholder={t('platform.cards.widgets.sword_shield_armor_helmet_placeholder')}
            disabled={isSubmitting}
            size={8}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Enter new tags separated by commas
          </p>
        </div>

        {/* Preview */}
        {newTags.trim() && (
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Preview
            </label>
            <div className="flex flex-wrap gap-1.5 p-2 bg-muted rounded border">
              {newTags
                .split(",")
                .map(tag => tag.trim())
                .filter(tag => tag.length > 0)
                .map((tag, index) => (
                  <span
                    key={index}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium text-foreground"
                    style={{ backgroundColor: groupColor }}
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-foreground bg-opacity-40" />
                    {tag}
                  </span>
                ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Button 
          onClick={handleSubmit} 
          size="sm" 
          className="flex items-center gap-1"
          disabled={isSubmitting || !newTags.trim()}
        >
          <Plus className="h-3 w-3" />
          {isSubmitting ? "Adding..." : "Add Tags"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
